import type {
    AdvancedFontSize,
    AdvancedFontWeight,
    AdvancedHeadingLetterSpacing,
    AdvancedHeadingWeight,
    AdvancedLetterSpacing,
    AdvancedLineHeight,
    AdvancedStyling,
} from '$lib/types/Theming/AdvancedStyling';

const FONT_WEIGHT_CLASSES: Record<AdvancedFontWeight, string> = {
    normal: 'fun-font-normal',
    medium: 'fun-font-medium',
    semibold: 'fun-font-semibold',
    bold: 'fun-font-bold',
    extrabold: 'fun-font-extrabold',
};

const HEADING_WEIGHT_CLASSES: Record<AdvancedHeadingWeight, string> = {
    light: 'fun-font-light',
    normal: 'fun-font-normal',
    medium: 'fun-font-medium',
    semibold: 'fun-font-semibold',
    bold: 'fun-font-bold',
    extrabold: 'fun-font-extrabold',
};

const FONT_SIZE_CLASSES: Record<AdvancedFontSize, string> = {
    xs: 'fun-text-xs',
    sm: 'fun-text-sm',
    base: 'fun-text-base',
    lg: 'fun-text-lg',
    xl: 'fun-text-xl',
    '2xl': 'fun-text-2xl',
};

const LETTER_SPACING_CSS: Record<AdvancedLetterSpacing, string> = {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
};

const HEADING_LETTER_SPACING_CSS: Record<AdvancedHeadingLetterSpacing, string> = {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
};

const LINE_HEIGHT_CSS: Record<AdvancedLineHeight, string> = {
    tight: '1.25',
    normal: '1.5',
    relaxed: '1.625',
};

export interface AdvancedStylingTextResult {
    // Tailwind class string (font-weight + font-size for body; font-weight for heading).
    className: string;
    // Inline style record for values without Tailwind equivalents (letter-spacing, line-height).
    style: Record<string, string>;
}

interface Options {
    // When true, applies headingWeight + headingLetterSpacing and skips body-only fields
    // (fontSize, lineHeight, textTransform — textTransform is handled by getAdvancedStylingClasses).
    heading?: boolean;
}

// Builds typography classes + inline styles from an AdvancedStyling object, matching the React
// editor's `getAdvancedStylingTextStyles(styling, { heading? })` behaviour.
export const getAdvancedStylingTextStyles = (
    styling: AdvancedStyling | undefined,
    { heading = false }: Options = {},
): AdvancedStylingTextResult => {
    if (!styling) {
        return { className: '', style: {} };
    }

    const classes: string[] = [];
    const style: Record<string, string> = {};

    if (heading) {
        if (styling.headingWeight) {
            classes.push(HEADING_WEIGHT_CLASSES[styling.headingWeight]);
        }

        if (styling.headingLetterSpacing) {
            style['letter-spacing'] = HEADING_LETTER_SPACING_CSS[styling.headingLetterSpacing];
        }

        return { className: classes.join(' '), style };
    }

    if (styling.fontWeight) {
        classes.push(FONT_WEIGHT_CLASSES[styling.fontWeight]);
    }

    if (styling.fontSize) {
        classes.push(FONT_SIZE_CLASSES[styling.fontSize]);
    }

    if (styling.letterSpacing) {
        style['letter-spacing'] = LETTER_SPACING_CSS[styling.letterSpacing];
    }

    if (styling.lineHeight) {
        style['line-height'] = LINE_HEIGHT_CSS[styling.lineHeight];
    }

    return { className: classes.join(' '), style };
};
